/**
 * useGcalSync.js — Google Calendar sync hooks
 *
 * Hooks for syncing jobs to Google Calendar and checking sync status.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { logAction } from '../lib/actionLogger';

/**
 * Check if Google Calendar integration is configured.
 */
export function useGcalConfig() {
  return useQuery({
    queryKey: ['gcal_config'],
    queryFn: () => api.get('/gcal/config'),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Get sync status for a specific job.
 */
export function useGcalStatus(jobId) {
  return useQuery({
    queryKey: ['gcal_status', jobId],
    queryFn: () => api.get(`/gcal/status/${jobId}`),
    enabled: !!jobId,
    staleTime: 30 * 1000,
  });
}

/**
 * Sync a job to Google Calendar.
 */
export function useSyncToGcal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId, silent = false }) =>
      api.post(`/gcal/sync/${jobId}`, { silent }),
    onSuccess: (data, { jobId, jobTitle }) => {
      qc.invalidateQueries({ queryKey: ['gcal_status', jobId] });
      logAction({
        action: 'Sync to Google Calendar',
        description: `Synced job ${jobTitle || jobId} to Google Calendar — ${data?.created ?? 0} created, ${data?.updated ?? 0} updated, ${data?.deleted ?? 0} deleted`,
        details: {
          created: data?.created,
          updated: data?.updated,
          deleted: data?.deleted,
        },
        jobId,
        jobTitle,
      });
    },
  });
}

/**
 * Remove a job from Google Calendar.
 */
export function useRemoveFromGcal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (jobId) => api.del(`/gcal/sync/${jobId}`),
    onSuccess: (data, jobId) => {
      qc.invalidateQueries({ queryKey: ['gcal_status', jobId] });
      logAction({
        action: 'Remove from Google Calendar',
        description: `Removed job ${jobId} from Google Calendar — ${data?.deleted ?? 0} event(s) deleted`,
        details: {
          deleted: data?.deleted,
        },
        jobId,
      });
    },
  });
}

/**
 * Create the shared RFOS calendar (admin one-time setup).
 */
export function useGcalSetup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/gcal/setup'),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['gcal_config'] });
      logAction({
        action: 'Set Up Google Calendar',
        description: `Created shared RFOS Google Calendar (calendarId ${data?.calendarId})`,
        details: {
          calendarId: data?.calendarId,
        },
      });
    },
  });
}
